import { useState, useEffect, useRef } from 'react';
import gsap from 'gsap';
// ── Hero ──────────────────────────────────────────────────────────────────────
// HeroNeon      = fullscreen video + neon script wordmark (current).
// HeroIrisStage = the original lens/iris hero, preserved verbatim as the backup.
// To revert: uncomment the HeroIrisStage import and swap the component in the
// #scene-hero block below. Nothing else changes.
import HeroNeon from '../components/home/HeroNeon.jsx';
// import HeroIrisStage from '../components/home/HeroIrisStage.jsx';
// WORK section (the filmstrip linking to Photography / Videography / Store) is
// removed from the page. FilmStripNav.jsx is untouched — to bring it back,
// uncomment this import and the scene-menu block and NAV_ITEMS entry below.
// import FilmStripNav from '../components/home/FilmStripNav.jsx';
import SceneFilm from '../components/home/SceneFilm.jsx';
import SceneGallery from '../components/home/SceneGallery.jsx';
import Manifesto from '../components/home/Manifesto.jsx';
import Press from '../components/home/Press.jsx';
import SceneStore from '../components/home/SceneStore.jsx';
import ContactClapper from '../components/home/ContactClapper.jsx';
import FrameCounter from '../components/shell/FrameCounter.jsx';
import { useSmoothScroll } from '../hooks/useSmoothScroll.js';

const DESIGN_W = 1440;
const DESIGN_H = 900;

// The side nav is fixed at right:28 and ~60px wide, so it floats over whatever the
// stage draws beneath it. When width was the limiting factor the stage filled the
// viewport edge to edge and ran under the rail — full-bleed rows collided with the
// active section's label. Reserving a gutter each side keeps the stage centred and
// clear of it; when height is limiting this is a no-op, the height term wins.
const SIDE_GUTTER = 100;
const fitScale = () =>
  Math.min((window.innerWidth - SIDE_GUTTER * 2) / DESIGN_W, window.innerHeight / DESIGN_H);

const NAV_ITEMS = [
  { id: 'scene-hero',    label: 'LENS'    },
  // { id: 'scene-menu',    label: 'WORK'    },   // restore with the scene below
  { id: 'scene-about',   label: 'ABOUT'   },
  { id: 'scene-gallery', label: 'GALLERY' },
  { id: 'scene-film',    label: 'FILM'    },
  { id: 'scene-press',   label: 'PRESS'   },
  { id: 'scene-store',   label: 'STORE'   },
  { id: 'scene-contact', label: 'CONTACT' },
];

// SceneStage wraps each 100vh section; contentRef gives GSAP a handle on the
// scaled inner canvas so we can animate it independently of the snap container.
// On mobile (isMobile=true) the fixed-canvas scaling is bypassed — each scene
// fills 100svh with its own native responsive layout.
function SceneStage({ id, scale, children, contentRef, isMobile }) {
  return (
    <section
      id={id}
      style={{
        height: '100svh',
        scrollSnapAlign: 'start',
        position: 'relative',
        overflow: 'hidden',
        background: '#000',
        flexShrink: 0,
      }}
    >
      {isMobile ? (
        <div
          ref={contentRef}
          style={{ width: '100%', height: '100%', position: 'relative', willChange: 'opacity' }}
        >
          {children}
        </div>
      ) : (
        // Two elements on purpose. The outer one carries the responsive fit and
        // nothing animates it; the reveal below runs on the inner one. They used
        // to be the same node, so GSAP's `scale: 1` overwrote the fit transform
        // and every revealed scene snapped to a full 1440x900 — overflowing the
        // viewport and pushing the top-right CTA off-screen entirely.
        <div
          style={{
            position: 'absolute',
            width: DESIGN_W,
            height: DESIGN_H,
            top: '50%',
            left: '50%',
            transform: `translate(-50%, -50%) scale(${scale})`,
            transformOrigin: 'center center',
          }}
        >
          <div
            ref={contentRef}
            style={{
              width: '100%',
              height: '100%',
              position: 'relative',
              willChange: 'opacity, transform',
            }}
          >
            {children}
          </div>
        </div>
      )}
    </section>
  );
}

export default function Home() {
  useSmoothScroll(false);

  const scrollRef  = useRef(null);

  // Refs to each section's inner canvas — animated by GSAP on reveal
  const contentRefs = useRef({});

  const [scale, setScale] = useState(() => fitScale());
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [activeScene, setActiveScene] = useState(0);

  // ── Resize ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const calc = () => {
      setScale(fitScale());
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, []);

  // ── Active scene tracking ────────────────────────────────────────────────
  useEffect(() => {
    const host = scrollRef.current;
    if (!host) return;
    const sections = NAV_ITEMS.map(({ id }) => document.getElementById(id)).filter(Boolean);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = NAV_ITEMS.findIndex(({ id }) => id === entry.target.id);
            if (idx !== -1) setActiveScene(idx);
          }
        });
      },
      { root: host, threshold: 0.3 }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  // ── Set hidden initial state on all non-hero scenes ──────────────────────
  // (runs once after mount so scenes aren't visible until they're entered)
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    NAV_ITEMS.slice(1).forEach(({ id }) => {
      const el = contentRefs.current[id];
      if (el) gsap.set(el, { opacity: 0, y: 52, scale: 0.93 });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Scene reveal animation ───────────────────────────────────────────────
  // Each non-hero scene animates in the first time it becomes active.
  // On revisit the canvas is already at its natural state so the tween is a no-op.
  useEffect(() => {
    const id = NAV_ITEMS[activeScene]?.id;
    if (!id || id === 'scene-hero') return;
    const el = contentRefs.current[id];
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    gsap.to(el, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 1.05,
      ease: 'expo.out',
      overwrite: 'auto',
    });
  }, [activeScene]);

  const scrollToScene = (id) => {
    const el = document.getElementById(id);
    const host = scrollRef.current;
    if (!el || !host) return;
    host.scrollTo({ top: el.offsetTop, behavior: 'smooth' });
  };

  // Helper: register a section's inner canvas ref by scene id
  const setRef = (id) => (el) => { contentRefs.current[id] = el; };

  return (
    <>
      {/* Film frame counter — bottom left */}
      <FrameCounter scrollHostRef={scrollRef} />

      {/* Side nav dots */}
      <nav
        className="home-side-nav"
        style={{
          position: 'fixed', right: 28, top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 9990,
          display: 'flex', flexDirection: 'column', gap: 18,
          alignItems: 'flex-end',
        }}
        aria-label="Page sections"
      >
        {NAV_ITEMS.map(({ id, label }, i) => (
          <button
            key={id}
            onClick={() => scrollToScene(id)}
            title={label}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 10,
              padding: 0,
            }}
          >
            {activeScene === i && (
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: 9,
                letterSpacing: '0.2em', color: 'rgba(242,239,232,0.45)',
                textTransform: 'uppercase',
              }}>
                {label}
              </span>
            )}
            <span style={{
              display: 'block',
              width:  activeScene === i ? 20 : 5,
              height: activeScene === i ? 2  : 5,
              borderRadius: activeScene === i ? 1 : '50%',
              background: activeScene === i ? '#ff2a1f' : 'rgba(242,239,232,0.2)',
              transition: 'all 400ms cubic-bezier(0.16, 1, 0.3, 1)',
            }} />
          </button>
        ))}
      </nav>

      {/* Scroll host */}
      <div
        ref={scrollRef}
        style={{
          height: '100vh',
          overflowY: 'scroll',
          scrollSnapType: 'none',
          scrollBehavior: 'smooth',
          background: '#000',
        }}
      >
        {/* Hero: 200vh so the hero holds while you scroll, sticky inner */}
        <div
          id="scene-hero"
          style={{ height: '200vh', scrollSnapAlign: 'start', position: 'relative' }}
        >
          <div style={{ position: 'sticky', top: 0, height: '100svh', overflow: 'hidden' }}>
            {/* To revert to the lens/iris hero, swap this line for:
                <HeroIrisStage scrollRef={scrollRef} isMobile={isMobile} /> */}
            <HeroNeon onContact={() => scrollToScene('scene-contact')} />
          </div>
        </div>

        {/* WORK section — restore by uncommenting this and the two lines above:
        <SceneStage id="scene-menu"    scale={scale} contentRef={setRef('scene-menu')}    isMobile={isMobile}>
          <FilmStripNav width={DESIGN_W} height={DESIGN_H} isMobile={isMobile} />
        </SceneStage>
        */}

        <SceneStage id="scene-about"   scale={scale} contentRef={setRef('scene-about')}   isMobile={isMobile}>
          <Manifesto width={DESIGN_W} height={DESIGN_H} isMobile={isMobile} />
        </SceneStage>

        <SceneStage id="scene-gallery" scale={scale} contentRef={setRef('scene-gallery')} isMobile={isMobile}>
          <SceneGallery width={DESIGN_W} height={DESIGN_H} isMobile={isMobile} />
        </SceneStage>

        <SceneStage id="scene-film"    scale={scale} contentRef={setRef('scene-film')}    isMobile={isMobile}>
          <SceneFilm width={DESIGN_W} height={DESIGN_H} isMobile={isMobile} />
        </SceneStage>

        <SceneStage id="scene-press"   scale={scale} contentRef={setRef('scene-press')}   isMobile={isMobile}>
          <Press width={DESIGN_W} height={DESIGN_H} isMobile={isMobile} />
        </SceneStage>

        <SceneStage id="scene-store"   scale={scale} contentRef={setRef('scene-store')}   isMobile={isMobile}>
          <SceneStore width={DESIGN_W} height={DESIGN_H} isMobile={isMobile} />
        </SceneStage>

        <SceneStage id="scene-contact" scale={scale} contentRef={setRef('scene-contact')} isMobile={isMobile}>
          <ContactClapper width={DESIGN_W} height={DESIGN_H} isMobile={isMobile} />
        </SceneStage>
      </div>

    </>
  );
}
